// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Info, AlertTriangle, ArrowLeft, ArrowRight } from 'lucide-react'
import { InfoBlock }      from '@/components/ui/InfoBlock'
import { PrimaryActionButton } from '@/components/ui/PrimaryActionButton'
import { SecondaryActionButton } from '@/components/ui/SecondaryActionButton'
import { RegieCoachBlock } from '@/components/process/RegieCoachBlock'
import { ClosingGuidance } from '@/components/process/ClosingGuidance'
import { InfoButton }     from '@/components/ui/InfoButton'
import { SaveStatusBadge } from '@/components/ui/SaveStatusBadge'
import { ModuleShell }    from '@/components/ui/ModuleShell'
import { Textarea } from '@/components/ui'
import { Input }          from '@/components/ui/Input'
import { RadioGroup }     from '@/components/ui/RadioGroup'
import { Card }           from '@/components/ui/Card'
import { LoadingState }   from '@/components/feedback/LoadingState'
import { useAuthStore, useWizardStore } from '@/store'
import { useAutosave }    from '@/hooks/useAutosave'
import { loadMedicalWishes, saveMedicalWishes, EMPTY_MEDICAL, type MedicalWishesData } from '@/services/wishes'
import { loadTrustedPersons } from '@/services/trustedPerson'
import { ROUTES, WIZARD_ROUTE } from '@/lib/constants/routes'
import { useReturnNav } from '@/lib/returnNav'

const RESUSCITATION_OPTIONS = [
  { value: 'yes',     label: 'Ja, reanimeer mij',           description: 'Ik wil gereanimeerd worden bij hartstilstand.' },
  { value: 'no',      label: 'Nee, niet reanimeren (DNAR)', description: 'Ik wil geen reanimatiepoging.' },
  { value: 'discuss', label: 'Bespreek met mijn arts',      description: 'Laat de beslissing afhangen van de situatie.' },
]
const DONOR_OPTIONS = [
  { value: 'yes',     label: 'Ja' },
  { value: 'no',      label: 'Nee' },
  { value: 'unknown', label: 'Weet ik niet' },
]

// Punt 19 - voorbeelden behandelgrenzen
const TREATMENT_EXAMPLES = [
  'Geen kunstmatige beademing zonder uitzicht op herstel.',
  'Geen langdurige IC-opname als herstel niet realistisch is.',
  'Comfortzorg staat voorop - pijnbestrijding boven levensverlengend handelen.',
  'Beslissingen in overleg met arts en familie.',
  'Ik wil thuis overlijden als dat medisch verantwoord is.',
]

// V50.2: relatie -> aanspreekvorm ("uw partner/dochter...") + voornaamwoord (haar/hem) voor een warmere bevestiging.
const REL_INFO: Record<string, { noun?: string; gender?: 'm' | 'f' }> = {
  PARTNER:   { noun: 'partner' },
  CHILD:     { noun: 'kind' },
  VADER:     { noun: 'vader',   gender: 'm' },
  MOEDER:    { noun: 'moeder',  gender: 'f' },
  BROER:     { noun: 'broer',   gender: 'm' },
  ZUS:       { noun: 'zus',     gender: 'f' },
  OOM:       { noun: 'oom',     gender: 'm' },
  TANTE:     { noun: 'tante',   gender: 'f' },
  NEEF:      { noun: 'neef',    gender: 'm' },
  NICHT:     { noun: 'nicht',   gender: 'f' },
  VRIEND:    { noun: 'vriend',  gender: 'm' },
  VRIENDIN:  { noun: 'vriendin',gender: 'f' },
  BUURMAN:   { noun: 'buurman', gender: 'm' },
  BUURVROUW: { noun: 'buurvrouw',gender: 'f' },
  COLLEGA:   { noun: 'collega' },
  KENNIS:    { noun: 'kennis' },
  // SIBLING/FRIEND/OTHER bewust geen voorvoegsel (te onbepaald)
}
function relWord(rel: string): { noun?: string; gender?: 'm' | 'f' } {
  if (REL_INFO[rel]) return REL_INFO[rel]
  const r = (rel || '').toLowerCase()
  if (/dochter/.test(r))                return { noun: 'dochter', gender: 'f' }
  if (/zoon/.test(r))                   return { noun: 'zoon', gender: 'm' }
  if (/echtgenote/.test(r))             return { noun: 'echtgenote', gender: 'f' }
  if (/echtgenoot/.test(r))             return { noun: 'echtgenoot', gender: 'm' }
  if (/moeder/.test(r))                 return { noun: 'moeder', gender: 'f' }
  if (/vader/.test(r))                  return { noun: 'vader', gender: 'm' }
  if (/zus/.test(r))                    return { noun: 'zus', gender: 'f' }
  if (/broer/.test(r))                  return { noun: 'broer', gender: 'm' }
  if (/vriendin/.test(r))               return { noun: 'vriendin', gender: 'f' }
  if (/vriend/.test(r))                 return { noun: 'vriend', gender: 'm' }
  if (/partner/.test(r))                return { noun: 'partner' }
  if (/kind/.test(r))                   return { noun: 'kind' }
  return {}
}

export function MedicalWishesPage() {
  const { userId }  = useAuthStore()
  const navigate    = useNavigate()
  const { target, label, fromSource } = useReturnNav()
  const [data,      setData]      = useState<MedicalWishesData>(EMPTY_MEDICAL)
  const [isLoading, setIsLoading] = useState(true)
  const [status,    setStatus]    = useState<'NOT_STARTED'|'IN_PROGRESS'|'COMPLETE'>('NOT_STARTED')
  const [primaryVP, setPrimaryVP] = useState('')
  const [reserveVP, setReserveVP] = useState('')
  const [primaryRel, setPrimaryRel] = useState('')
  const [reserveRel, setReserveRel] = useState('')

  useEffect(() => {
    if (!userId) return
    loadMedicalWishes(userId).then(loaded => {
      const merged = { ...EMPTY_MEDICAL, ...(loaded ?? {}) }
      // V35-P2g: prefill organDonation vanuit wizard-antwoord als Dexie-waarde leeg is
      // Bestaande Dexie-waarde wint altijd - nooit overschrijven
      if (!merged.organDonation) {
        const donorFromWizard = useWizardStore.getState().answers.donorStatus ?? ''
        if (donorFromWizard) merged.organDonation = donorFromWizard
      }
      setData(merged)
      recalc(merged)
      setIsLoading(false)
    })
    // Laad VP-namen voor medisch vertegenwoordiger koppeling
    loadTrustedPersons(userId).then(vps => {
      const active = vps.filter(v => v.confirmationStatus !== 'REVOKED')
      const primary = active.find(v => v.role === 'PRIMARY')
      const reserve = active.find(v => v.role === 'RESERVE')
      if (primary) { setPrimaryVP(primary.firstName); setPrimaryRel(String(primary.relationship ?? '')) }
      if (reserve) { setReserveVP(reserve.firstName); setReserveRel(String(reserve.relationship ?? '')) }
    })
  }, [userId])

  function recalc(d: MedicalWishesData) {
    const n = [d.treatmentPreferences, d.resuscitation, d.organDonation, d.gpName].filter(Boolean).length
    const p = Math.round((n / 4) * 100)
    setStatus(p === 0 ? 'NOT_STARTED' : p === 100 ? 'COMPLETE' : 'IN_PROGRESS')
  }

  const save = useCallback(async (d: MedicalWishesData) => {
    if (!userId) return
    await saveMedicalWishes(userId, d)
  }, [userId])

  const { status: saveStatus, saveNow } = useAutosave({ data, onSave: save, enabled: !isLoading })

  const upd = useCallback(<K extends keyof MedicalWishesData>(k: K, v: MedicalWishesData[K]) => {
    setData(prev => { const n = { ...prev, [k]: v }; recalc(n); return n })
  }, [])

  if (isLoading) return <LoadingState message="Laden..." />

  return (
    <div>
      <button onClick={async () => { await saveNow(); navigate(ROUTES.DASHBOARD) }}
        className="flex items-center gap-1.5 text-sm text-ink-4 hover:text-ink-3 transition-colors mb-5 group">
        <svg className="h-4 w-4 group-hover:-translate-x-0.5 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
        Terug naar dashboard
      </button>
      <ModuleShell title="Medische wensen"
        description="Leg uw behandelgrenzen en medische wensen vast als leidraad voor uw naasten en artsen." status={status}
        actions={<SaveStatusBadge status={saveStatus} />}
      >
        {/* V50: emotionele begeleiding direct onder de intro */}
        <p className="text-sm text-ink-2 leading-relaxed">
          Leg uw medische wensen vast zolang u zelf uw keuzes kunt maken. Zo helpt u uw naasten en
          zorgverleners om later beslissingen te nemen die zoveel mogelijk aansluiten bij uw eigen wensen.
        </p>

        {/* 1. Waarom dit belangrijk is - vast, altijd zichtbaar (niet inklapbaar) */}
        <div className="rounded-xl border border-l-4 border-status-infoBorder border-l-status-infoText bg-status-infoBg overflow-hidden">
          <div className="flex items-start gap-3 px-4 py-3.5">
            <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-status-infoText/10 text-status-infoText">
              <Info className="h-4 w-4" aria-hidden="true" />
            </span>
            <div className="flex-1">
              <p className="text-sm font-semibold text-ink-2 leading-snug mb-1.5">Waarom dit belangrijk is</p>
              <p className="text-[13px] leading-relaxed text-ink-3">
                WilsApp helpt u om uw medische wensen overzichtelijk vast te leggen. Sommige wensen, zoals een schriftelijke wilsverklaring of een levenstestament, moeten daarnaast officieel worden vastgelegd en besproken met uw huisarts, specialist of notaris.
              </p>
            </div>
          </div>
        </div>

        {/* 2. Behandelgrenzen */}
        <Card>
          <InfoBlock title="Goed om te weten - behandelgrenzen" variant="blue" defaultOpen={false}>
            <p>Er kan een moment komen waarop u zelf niet meer kunt zeggen wat u wilt. Behandelgrenzen geven dan aan wat u wel en niet wilt als u ernstig ziek bent. Bespreek deze wensen ook met uw huisarts; een wens die niemand kent, kan later niet worden gevolgd.</p>
            <p><strong>Voorbeelden die u kunt vastleggen:</strong></p>
            {TREATMENT_EXAMPLES.map((ex, i) => (
              <p key={i}>• {ex}</p>
            ))}
          </InfoBlock>
          <h2 className="text-base font-semibold text-ink mt-4 mb-1">Levensverlengende behandeling</h2>
          <p className="text-sm text-ink-3 mb-4">Wanneer ernstig ziek - wilt u alles gedaan hebben om uw leven te verlengen?</p>
          <Textarea label="Mijn wensen" value={data.treatmentPreferences}
            onChange={e => upd('treatmentPreferences', e.target.value)}
            placeholder="Bijv: Ik wil geen kunstmatige beademing als herstel niet realistisch is..." maxLength={1000} showCount />
        </Card>

        {/* 3. Reanimatie */}
        <Card>
          <h2 className="text-base font-semibold text-ink mb-1">Reanimatie</h2>
          <p className="text-sm text-ink-3 mb-4">Wilt u gereanimeerd worden bij een hartstilstand?</p>
          <RadioGroup name="resuscitation" options={RESUSCITATION_OPTIONS} value={data.resuscitation} onChange={v => upd('resuscitation', v)} />
          {data.resuscitation === 'no' && (
            <div className="mt-4 flex items-start gap-2.5 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3">
              <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
              <div>
                <p className="text-xs font-semibold text-amber-800 mb-0.5">Belangrijk</p>
                <p className="text-xs text-amber-700 leading-relaxed">
                  Deze keuze in WilsApp legt uw persoonlijke wens vast, maar is geen officiële medische opdracht.
                  Bespreek een niet-reanimerenwens altijd met uw huisarts en leg deze schriftelijk vast.
                  Informeer ook uw naasten hierover.
                </p>
              </div>
            </div>
          )}
        </Card>

        {/* 4. Orgaandonatie */}
        <Card>
          <h2 className="text-base font-semibold text-ink mb-1">Orgaandonatie</h2>
          <p className="text-sm text-ink-3 mb-4">Bent u geregistreerd in het Donorregister?</p>
          <RadioGroup name="organDonation" options={DONOR_OPTIONS} value={data.organDonation} onChange={v => upd('organDonation', v)} />
          <div className="mt-4 flex items-start gap-2.5 rounded-xl bg-status-infoBg border border-sage-200 px-4 py-3.5">
            <Info className="h-4 w-4 text-status-infoText flex-shrink-0 mt-0.5" />
            <p className="text-xs text-status-infoText leading-relaxed">
              Uw keuze over orgaandonatie wordt officieel vastgelegd in het landelijke Donorregister.
              WilsApp kan deze registratie niet voor u vastleggen of wijzigen.
            </p>
          </div>
          <div className="mt-4">
            <Textarea label="Persoonlijke toelichting voor mijn naasten"
              value={data.organDonationDetails} onChange={e => upd('organDonationDetails', e.target.value)}
              placeholder="Bijv: Ik wil graag dat mijn organen zoveel mogelijk mensen kunnen helpen..." maxLength={400} />
          </div>
        </Card>

        {/* 5. Voorkeur voor zorg */}
        <Card>
          <h2 className="text-base font-semibold text-ink mb-4">Voorkeur voor zorg</h2>
          <Textarea label="Voorkeur voor zorglocatie en omgeving" value={data.hospicePreference}
            onChange={e => upd('hospicePreference', e.target.value)}
            placeholder="Bijv: Ik wil het liefst thuis overlijden..." maxLength={500} />
        </Card>

        {/* 6. Medisch vertegenwoordiger met VP-koppeling */}
        <Card>
          <div className="flex items-center gap-2 mb-1">
            <h2 className="text-base font-semibold text-ink">Medisch vertegenwoordiger</h2>
            <InfoButton title="Medisch vertegenwoordiger"
              content={<>
                <p>Als u niet meer kunt communiceren, mag uw medisch vertegenwoordiger namens u beslissingen nemen.</p>
                <p><strong>Volgorde wettelijk:</strong> schriftelijk gevolmachtigde, dan echtgenoot of partner, dan ouder, kind, broer of zus.</p>
              </> } />
          </div>
          <p className="text-sm text-ink-3 mb-4">Wie mag namens u beslissen als u dat zelf niet meer kunt?</p>

          <div className="flex flex-col gap-2 mb-4">
            {[
              { value: 'primary_vp', label: `Eerste vertrouwenspersoon${primaryVP ? ` - ${primaryVP}` : ''}`, disabled: !primaryVP },
              { value: 'reserve_vp', label: `Tweede vertrouwenspersoon${reserveVP ? ` - ${reserveVP}` : ''}`, disabled: !reserveVP },
              { value: 'other',      label: 'Andere persoon invoeren', disabled: false },
            ].filter(o => !o.disabled || data.proxySource === o.value).map(opt => (
              <button key={opt.value} type="button"
                onClick={() => !opt.disabled && upd('proxySource', opt.value)}
                className={`flex items-center gap-3 rounded-xl border-2 px-4 py-3 text-left transition-all ${
                  data.proxySource === opt.value ? 'border-sage-600 bg-sage-50' :
                  opt.disabled ? 'border-sand-200 opacity-50 cursor-not-allowed' :
                  'border-sand-300 hover:border-sage-300'
                }`}>
                <div className={`h-4 w-4 rounded-full border-2 flex-shrink-0 ${
                  data.proxySource === opt.value ? 'border-sage-600 bg-sage-600' : 'border-sand-300'
                }`} />
                <span className="text-sm text-ink">{opt.label}</span>
              </button>
            ))}
          </div>

          {data.proxySource === 'other' && (
            <div className="flex flex-col gap-3 animate-slide-down">
              <Input label="Naam en relatie" value={data.proxyDecisionMaker}
                onChange={e => upd('proxyDecisionMaker', e.target.value)} placeholder="Bijv: Jan de Vries - partner" />
              <div className="grid gap-3 sm:grid-cols-2">
                <Input label="Telefoonnummer" type="tel" value={data.proxyPhone}
                  onChange={e => upd('proxyPhone', e.target.value)} placeholder="06-..." />
                <Input label="E-mailadres" type="email" value={data.proxyEmail}
                  onChange={e => upd('proxyEmail', e.target.value)} placeholder="naam@mail.nl" />
              </div>
            </div>
          )}

          {(data.proxySource && data.proxySource !== '') && (
            <div className="rounded-xl bg-sage-50 border border-sage-100 px-4 py-3 animate-slide-down">
              <p className="text-xs text-sage-700 leading-relaxed">
                {(() => {
                  let naam = '', rel = ''
                  if (data.proxySource === 'primary_vp')      { naam = primaryVP; rel = primaryRel }
                  else if (data.proxySource === 'reserve_vp') { naam = reserveVP; rel = reserveRel }
                  else {
                    const parts = (data.proxyDecisionMaker || '').split(' - ')
                    naam = (parts[0] || '').trim(); rel = (parts[1] || '').trim()
                  }
                  const info   = relWord(rel)
                  const prefix = info.noun ? `uw ${info.noun} ` : ''
                  const pron   = info.gender === 'f' ? 'haar' : info.gender === 'm' ? 'hem' : 'hem of haar'
                  const open   = naam
                    ? `U heeft ${prefix}${naam} aangewezen als uw medisch vertegenwoordiger. `
                    : 'U heeft een medisch vertegenwoordiger aangewezen. '
                  return `${open}Bespreek uw wensen met ${pron} en laat weten waar belangrijke documenten zijn opgeslagen. Controleer regelmatig of deze keuze nog aansluit bij uw situatie.`
                })()}
              </p>
            </div>
          )}

          {/* Tweede medisch vertegenwoordiger */}
          {(data.proxySource && data.proxySource !== '') && (
            <div className="flex flex-col gap-3 border-t border-sand-200 pt-3 animate-slide-down">
              <p className="text-xs font-semibold text-ink-4 uppercase tracking-wider">Tweede vertegenwoordiger (optioneel)</p>
              <p className="text-xs text-ink-4">Als de primaire vertegenwoordiger niet bereikbaar is, wie neemt dan de beslissing?</p>
              <div className="flex flex-col gap-2">
                {[
                  { value: 'reserve_vp', label: `Reserve vertrouwenspersoon${reserveVP ? ` - ${reserveVP}` : ''}`, disabled: !reserveVP },
                  { value: 'other',      label: 'Andere persoon invoeren', disabled: false },
                ].filter(o => !o.disabled || data.reserveProxySource === o.value).map(opt => (
                  <button key={opt.value} type="button"
                    onClick={() => !opt.disabled && upd('reserveProxySource', opt.value)}
                    className={`flex items-center gap-3 rounded-xl border-2 px-4 py-2.5 text-left transition-all ${
                      data.reserveProxySource === opt.value ? 'border-sage-600 bg-sage-50' :
                      opt.disabled ? 'border-sand-200 opacity-50 cursor-not-allowed' :
                      'border-sand-300 hover:border-sage-300'
                    }`}>
                    <div className={`h-4 w-4 rounded-full border-2 flex-shrink-0 ${
                      data.reserveProxySource === opt.value ? 'border-sage-600 bg-sage-600' : 'border-sand-300'
                    }`} />
                    <span className="text-sm text-ink">{opt.label}</span>
                  </button>
                ))}
              </div>
              {data.reserveProxySource === 'other' && (
                <div className="flex flex-col gap-3 animate-slide-down">
                  <Input label="Naam en relatie" value={data.reserveProxyName}
                    onChange={e => upd('reserveProxyName', e.target.value)} placeholder="Bijv: Jan de Vries - broer" />
                  <div className="grid gap-3 sm:grid-cols-2">
                    <Input label="Telefoonnummer" type="tel" value={data.reserveProxyPhone}
                      onChange={e => upd('reserveProxyPhone', e.target.value)} placeholder="06-..." />
                    <Input label="E-mailadres" type="email" value={data.reserveProxyEmail}
                      onChange={e => upd('reserveProxyEmail', e.target.value)} placeholder="naam@mail.nl" />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Notarisdossier koppeling */}
          {(data.proxySource && data.proxySource !== '') && (
            <div className="flex items-center gap-2 rounded-xl bg-sage-50 border border-sage-200 px-3 py-2">
              <span className="text-xs text-sage-700">✓ Deze keuze wordt opgenomen in uw notarisdossier.</span>
            </div>
          )}

          {/* Medische volmacht infoblok (losse 'Aandachtspunt'-amber verwijderd; deze info volstaat) */}
          <InfoBlock title="Medische volmacht" variant="warning" defaultOpen={true}>
            <p className="text-xs text-ink-3 leading-relaxed">
              Een aanwijzing in WilsApp legt uw wens vast, maar maakt iemand nog niet automatisch uw
              wettelijke vertegenwoordiger. Daarvoor legt u de aanwijzing vast in een schriftelijke
              <strong> medische volmacht</strong>. Zo'n volmacht is geldig zonder notaris, maar kan ook via
              de notaris, bijvoorbeeld als onderdeel van een levenstestament. Zorg er ook voor dat uw
              (huis)arts weet wie uw vertegenwoordiger is, zodat dit in uw medisch dossier komt te staan.
            </p>
            <a href="https://www.rijksoverheid.nl/onderwerpen/levenseinde-en-euthanasie/vraag-en-antwoord/wilsonbekwaamheid"
              target="_blank" rel="noopener noreferrer"
              className="mt-2 inline-flex items-center gap-1 text-xs text-sage-600 underline underline-offset-2 hover:text-sage-800">
              Uitleg op Rijksoverheid.nl →
            </a>
            <a href="https://www.knb.nl/kennisbank/familie-erfrecht/levenstestament/"
              target="_blank" rel="noopener noreferrer"
              className="mt-1 block text-xs text-sage-600 underline underline-offset-2 hover:text-sage-800">
              Ook mogelijk via de notaris (KNB) →
            </a>
          </InfoBlock>
        </Card>

        {/* 7. Uw huisarts */}
        <Card>
          <h2 className="text-base font-semibold text-ink mb-1">Uw huisarts</h2>
          <p className="text-sm text-ink-3 mb-4">Bespreek uw medische wensen regelmatig met uw huisarts, zeker wanneer uw gezondheid verandert.</p>
          <div className="flex flex-col gap-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <Input label="Naam huisarts" value={data.gpName} onChange={e => upd('gpName', e.target.value)} placeholder="Dr. Jansen" />
              <Input label="Praktijknaam" value={data.gpPractice} onChange={e => upd('gpPractice', e.target.value)} placeholder="Huisartsenpraktijk De Hoek" />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <Input label="Telefoonnummer" type="tel" value={data.gpPhone} onChange={e => upd('gpPhone', e.target.value)} placeholder="020-1234567" />
              <Input label="Plaats" value={data.gpCity} onChange={e => upd('gpCity', e.target.value)} placeholder="Amsterdam" />
            </div>
          </div>
          <div className="mt-4 rounded-xl border border-l-4 border-emotional-border border-l-emotional-text bg-emotional-bg px-4 py-3.5">
            <p className="text-sm font-semibold text-ink-2 mb-1">💬 Een goed gesprek voorkomt moeilijke keuzes</p>
            <p className="text-[13px] leading-relaxed text-ink-3">
              Door uw wensen tijdig met uw huisarts te bespreken, helpt u uw naasten en zorgverleners om later beslissingen te nemen die zoveel mogelijk aansluiten bij wat u zelf belangrijk vindt.
            </p>
          </div>
        </Card>

        {/* 8. Schriftelijke wilsverklaring (losse 'Let op'-amber verwijderd; boodschap staat in 'Waarom dit belangrijk is') */}
        <Card>
          <h2 className="text-base font-semibold text-ink mb-1">Schriftelijke wilsverklaring</h2>
          <p className="text-xs text-ink-3 leading-relaxed mb-3">
            Een schriftelijke wilsverklaring kan belangrijk zijn wanneer u zelf niet meer kunt aangeven
            welke medische behandelingen u wel of niet wilt. Voor medische besluitvorming adviseren wij
            deze verklaring te bespreken met uw huisarts en een ondertekend exemplaar te bewaren.
          </p>

          <p className="text-xs font-semibold text-ink-4 uppercase tracking-wider mb-2">
            Heeft u een schriftelijke wilsverklaring?
          </p>
          <div className="flex flex-col gap-2 mb-3">
            {([
              { value: 'yes',     label: 'Ja, ik heb een schriftelijke wilsverklaring' },
              { value: 'pending', label: 'In voorbereiding' },
              { value: 'no',      label: 'Nee' },
            ] as const).map(opt => (
              <button key={opt.value} type="button"
                onClick={() => upd('livingWillExists', opt.value)}
                className={`flex items-center gap-3 rounded-xl border-2 px-4 py-2.5 text-left transition-all ${
                  data.livingWillExists === opt.value
                    ? 'border-sage-600 bg-sage-50'
                    : 'border-sand-300 hover:border-sage-300'
                }`}>
                <div className={`h-4 w-4 rounded-full border-2 flex-shrink-0 ${
                  data.livingWillExists === opt.value ? 'border-sage-600 bg-sage-600' : 'border-sand-300'
                }`} />
                <span className="text-sm text-ink">{opt.label}</span>
              </button>
            ))}
          </div>

          {(data.livingWillExists === 'yes' || data.livingWillExists === 'pending') && (
            <div className="flex flex-col gap-3 mt-1 animate-slide-down">
              <Input label="Waar wordt deze bewaard?" value={data.livingWillLocation}
                onChange={e => upd('livingWillLocation', e.target.value)}
                placeholder="Bijv: Rode map in de la, bij de notaris..." />
              <div className="grid gap-3 sm:grid-cols-2">
                <Input label="Notaris (optioneel)" value={data.livingWillNotaris}
                  onChange={e => upd('livingWillNotaris', e.target.value)}
                  placeholder="Naam notariskantoor" />
                <Input label="Datum opstellen (optioneel)" type="date" value={data.livingWillDate}
                  onChange={e => upd('livingWillDate', e.target.value)} />
              </div>
            </div>
          )}
        </Card>

        {/* Wat wilt u verder meegeven (hernoemd van 'Aanvullende wensen' / 'Overige medische wensen') */}
        <Card>
          <h2 className="text-base font-semibold text-ink mb-4">Wat wilt u uw naasten en artsen verder nog meegeven?</h2>
          <Textarea label="Persoonlijke toelichting" value={data.additionalWishes}
            onChange={e => upd('additionalWishes', e.target.value)}
            placeholder="Alles wat u uw naasten en artsen nog wilt meegeven..." maxLength={800} showCount />
        </Card>

        {/* 9. Meer informatie (euthanasie-verwijzing naar het einde) */}
        <Card>
          <h2 className="text-base font-semibold text-ink mb-1">Meer informatie</h2>
          <p className="text-sm text-ink-3 mb-3">Wilt u zich verder verdiepen? Lees meer over euthanasie, een behandelverbod en palliatieve sedatie.</p>
          <button type="button" onClick={() => navigate(ROUTES.EUTHANASIE_INFO)}
            className="flex items-center gap-2 self-start text-sm text-sage-700 hover:text-sage-800 hover:underline">
            <Info className="h-4 w-4 flex-shrink-0" />
            Meer weten over euthanasie, behandelverbod en palliatieve sedatie?
          </button>
        </Card>

        {/* V50: actualiseren-herinnering onderaan */}
        <div className="rounded-2xl bg-sand border border-sand-200 px-4 py-3.5">
          <p className="text-sm text-ink-3 leading-relaxed">
            Uw wensen kunnen veranderen. Controleer deze pagina daarom regelmatig, vooral wanneer uw gezondheid verandert of wanneer u een diagnose krijgt zoals dementie, Parkinson of een andere progressieve neurologische aandoening.
          </p>
        </div>

        {/* V50.3: afsluitend begeleidingsblok (zachtere, niet te definitieve titel) */}
        {status !== 'NOT_STARTED' && (
          <ClosingGuidance title="Uw medische wensen zijn opgeslagen">
            U kunt deze altijd aanpassen wanneer uw situatie of wensen veranderen. Door uw wensen nu vast te leggen helpt u uw naasten en zorgverleners later moeilijke keuzes te voorkomen.
          </ClosingGuidance>
        )}
      
      {/* Sticky save-status zone - zichtbaar wanneer gebruiker onderaan de pagina scrolt */}
      <div className="sticky bottom-0 left-0 right-0 flex items-center justify-end
                      bg-white/90 backdrop-blur-sm border-t border-sand-200
                      px-4 py-2.5 mt-6 -mx-1 rounded-b-2xl">
        <SaveStatusBadge status={saveStatus} />
      </div>
            <div className="flex flex-col gap-2 mt-2">
              {fromSource ? (
                <>
                  {/* Vanuit een interne verwijzing geopend: terug naar de bron is de logische vervolgstap */}
                  <PrimaryActionButton onClick={async () => { await saveNow(); navigate(target) }} icon={<ArrowLeft className="h-5 w-5" />}>
                    {label}
                  </PrimaryActionButton>
                  <SecondaryActionButton onClick={async () => { await saveNow(); navigate(-1) }}>Terug naar vorige scherm</SecondaryActionButton>
                </>
              ) : (
                <>
                  {/* Rechtstreeks/dashboard: doorgaan met de begeleide wizard is de meest logische vervolgstap */}
                  <PrimaryActionButton onClick={() => navigate(WIZARD_ROUTE)} iconRight={<ArrowRight className="h-5 w-5" />}>
                    Verder in de begeleide wizard
                  </PrimaryActionButton>
                  <SecondaryActionButton icon={null} onClick={async () => { await saveNow(); navigate(ROUTES.DASHBOARD) }}>
                    Klaar - terug naar dashboard
                  </SecondaryActionButton>
                </>
              )}
            </div>
            <RegieCoachBlock page="medical" />
    </ModuleShell>
    </div>
  )
}
